import React, { useState } from 'react';

import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RoutePlanMissingPriceDTO } from 'api/model';
import useTranslations from 'contexts/basicData/useTranslations';
import styled from 'styled-components';

const Wrapper = styled.div`
  border: 1px solid rgb(240, 200, 120);
  border-left: 4px solid rgb(225, 165, 30);
  border-radius: 3px;
  background: rgb(255, 250, 238);
  padding: 8px 10px;
  margin-bottom: 8px;
  font-size: 0.9em;
`;

const SummaryRow = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: rgb(120, 80, 0);
`;

const Strong = styled.span`
  font-weight: 700;
`;

const Toggle = styled.span`
  margin-left: auto;
  font-size: 0.85em;
  text-decoration: underline;
  white-space: nowrap;
`;

const Table = styled.table`
  border-collapse: collapse;
  margin-top: 8px;
  width: 100%;
  font-size: 0.95em;

  th,
  td {
    text-align: left;
    padding: 2px 10px 2px 0;
    border-bottom: 1px solid rgb(240, 225, 190);
  }

  td:last-child,
  th:last-child {
    text-align: right;
    padding-right: 0;
  }
`;

const Reg = styled.div`
  margin-top: 6px;
  color: rgb(130, 100, 40);
  font-size: 0.9em;
`;

const Note = styled.p`
  margin: 8px 0 0;
  color: rgb(130, 100, 40);
`;

interface Props {
  missingPrices: RoutePlanMissingPriceDTO[];
}

/**
 * Säger UT att prislistan saknas, i stället för att låta det se ut som ett planeringsproblem.
 *
 * "TB okänt" på ett paketkort säger inte varför, och skillnaden är avgörande för trafikledaren:
 * en prislucka är ekonomis ärende och ingenting att väga i planeringen, medan ett okänt TB av
 * andra skäl kan vara ett verkligt problem i paketet. Utan den här rutan går de två inte att
 * skilja åt, och TL får leta efter ett fel som inte finns.
 *
 * Listan är grupperad per läns- och typkombination, eftersom det är den formen luckan går att
 * ÅTGÄRDA i — en rad i en prislista täcker alla ärenden i kombinationen.
 */
const MissingPriceNotice: React.FC<Props> = ({ missingPrices }) => {
  const [expanded, setExpanded] = useState(false);
  const translations = useTranslations();

  if (missingPrices.length === 0) {
    return null;
  }

  const caseCount = missingPrices.reduce((sum, m) => sum + m.caseCount, 0);
  const filledCount = missingPrices.reduce(
    (sum, m) => sum + (m.filledWithLowestPriceCount ?? 0),
    0
  );

  return (
    <Wrapper>
      <SummaryRow onClick={() => setExpanded((prev) => !prev)} type="button">
        <FontAwesomeIcon icon={faTriangleExclamation} />
        <span>
          <Strong>
            {caseCount} {caseCount === 1 ? 'ärende' : 'ärenden'} saknar
            prislista
          </Strong>{' '}
          i {missingPrices.length}{' '}
          {missingPrices.length === 1 ? 'kombination' : 'kombinationer'} —{' '}
          {/* Schablonpriset (lägsta på sträckan) fyller en del av luckorna i planeringen; resten
              visas som okänt TB. Fakturaunderlaget påverkas inte. */}
          {filledCount > 0
            ? `${filledCount} av dem har schablonpris, övriga visas med okänt TB`
            : 'deras TB visas som okänt'}
        </span>
        <Toggle>{expanded ? 'Dölj' : 'Visa vilka'}</Toggle>
      </SummaryRow>

      {expanded && (
        <>
          <Table>
            <thead>
              <tr>
                <th>Ärendetyp</th>
                <th>Från län</th>
                <th>Till län</th>
                <th>Vad som saknas</th>
                <th>Ärenden</th>
              </tr>
            </thead>
            <tbody>
              {missingPrices.map((m) => (
                <tr key={`${m.caseTypeID}-${m.fromCounty}-${m.toCounty}`}>
                  <td>
                    {translations.caseTypes?.[m.caseTypeID] ?? m.caseTypeID}
                  </td>
                  <td>{m.fromCounty}</td>
                  <td>{m.toCounty}</td>
                  <td>
                    {m.isWithinCountyGap ? 'inomlänspris' : 'länspar/län'}
                  </td>
                  <td>{m.caseCount}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Reg>
            Uppdrag: {missingPrices.flatMap((m) => m.assignmentIds).join(', ')}
          </Reg>

          <Note>
            Det här är en lucka i <strong>prisdatat</strong>, inte i paketen.
            Paketen går att verkställa som vanligt — men deras täckningsbidrag
            går inte att väga mot andra förrän prislistan har raden. Rader
            märkta <em>inomlänspris</em> har ett län i prislistan men saknar
            pris för resor inom länet.
          </Note>
        </>
      )}
    </Wrapper>
  );
};

export default MissingPriceNotice;
